import { CreditBreakdownCreditType } from '@/@types/client-api';
import { FC, ReactNode, useMemo, useState } from 'react';
import classnames from 'classnames';
import Text from '@/components/cms/Text';
import { getCreditsText } from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import Divider from '@/components/ui/Divider';
import { TooltipBlockProps } from '../../Tooltip/TooltipBlock';
import { ConvertExpandedContent } from '@/utils/helpers';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import Icon from '@/components/ui/Icon';
import RichText from '@/components/cms/RichText';
import Link from '@/components/ui/Link';
import { IContent } from '@/@types/cms';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export type RequirementsSectionProps = {
  creditBreakdownCreditTypes?: CreditBreakdownCreditType[];
  allOtherCreditsTooltip?: IContent[];
  contentWrapBreakpoint?: keyof typeof ScreenSizeQueries;
  jurisdictionMessage?: string;
  className?: string;
};

const Requirements: FC<RequirementsSectionProps> = ({
  creditBreakdownCreditTypes,
  allOtherCreditsTooltip,
  jurisdictionMessage,
  className,
  contentWrapBreakpoint = 'md'
}) => {
  const isAtBreakpoint = useMediaQuery(ScreenSizeQueries[contentWrapBreakpoint]);
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);

  const allOtherCreditsTooltipComponent = useMemo(() => {
    const tooltipContent = ConvertExpandedContent<TooltipBlockProps>(
      allOtherCreditsTooltip?.[0] ?? {}
    );

    return (
      (!!tooltipContent?.title || !!tooltipContent?.copy || !!tooltipContent?.link) && (
        <ComplexTooltip>
          <ComplexTooltip.Trigger
            aria-label="Learn more about all other credits"
            className="relative z-10 ml-1 size-6 shrink-0 text-gray hover:text-gray-darker focus:text-gray-darker"
          >
            <Icon name="info" size="small" className="size-5" />
          </ComplexTooltip.Trigger>
          <ComplexTooltip.Content customContainer={contentContainer}>
            <div>
              {!!tooltipContent.title && (
                <RichText className="text-2 mb-1 font-bold" content={tooltipContent.title} />
              )}
              {!!tooltipContent.copy && (
                <RichText className="text-2" content={tooltipContent.copy} />
              )}
              {!!tooltipContent.link && (
                <Link
                  href={tooltipContent.link?.href}
                  text={tooltipContent.link?.text}
                  title={tooltipContent.link?.title}
                  target={tooltipContent.link?.target}
                />
              )}
            </div>
          </ComplexTooltip.Content>
        </ComplexTooltip>
      )
    );
  }, [allOtherCreditsTooltip, contentContainer]);

  const completeIconText = useMemo(
    () => (
      <span className="inline-flex flex-row items-center justify-start">
        <Icon name="approved" size="small" className="mr-2" />
        <Text className="text-2 text-serpentine">Complete</Text>
      </span>
    ),
    []
  );

  const wrapperClasses = classnames('flex w-full justify-between', {
    'flex-row items-center gap-4': isAtBreakpoint,
    'flex-col items-start': !isAtBreakpoint
  });

  const iterateSubRequirements = (types: CreditBreakdownCreditType[]): ReactNode => {
    const requiredTypes = types.filter(type => type?.isRequired || false);
    if (!requiredTypes.length) return null;

    return (
      <div className="pl-7">
        {requiredTypes.map((type, index) => (
          <div
            key={type.key ?? type.name ?? index}
            className={classnames('relative ml-2 mt-3', {
              'before:pointer-events-none before:absolute before:-bottom-3 before:-left-7 before:top-0 before:border-l before:border-silver':
                index < requiredTypes.length - 1
            })}
          >
            <div
              className={classnames(
                'relative before:pointer-events-none',
                'before:absolute before:-left-7 before:bottom-1/2 before:w-5 before:rounded-bl before:border-b before:border-l before:border-silver',
                {
                  'before:-top-4': index > 0,
                  'before:-top-2': index == 0
                }
              )}
            >
              <div className={wrapperClasses}>
                <Text className="text-1-medium mr-2 text-gray-dark md:w-1/2 lg:mr-0 lg:w-3/4">
                  {type.name}
                </Text>
                {type.isComplete ? (
                  completeIconText
                ) : (
                  <Text className="text-1 w-fit flex-1 whitespace-nowrap text-right text-gray-dark">
                    {getCreditsText(type.amount || 0, false, true)}
                  </Text>
                )}
              </div>
            </div>
            {iterateSubRequirements(type.subRequirements ?? [])}
          </div>
        ))}
      </div>
    );
  };

  return (
    (creditBreakdownCreditTypes?.length ?? 0) > 0 && (
      <div className={classnames(className, 'flex flex-col items-start justify-between bg-white')}>
        <div className="w-full">
          {creditBreakdownCreditTypes?.map((a, i) => {
            const isFirst = i === 0;
            const isLast = i === creditBreakdownCreditTypes.length - 1;
            const hasSubRequirements =
              (a.subRequirements?.filter(a => a.isRequired || false)?.length || 0) > 0;
            return (
              <div key={a.key ?? a.name ?? i}>
                {!isFirst && <Divider color="light" orientation="horizontal" />}
                {
                  <div
                    className={classnames({
                      'pt-6': !isFirst,
                      'pb-6': !isLast
                    })}
                  >
                    <div className={wrapperClasses}>
                      <div className="flex flex-row items-center justify-start">
                        <Text className="text-1-medium">{a.name}</Text>
                        {!!a.isAllOtherCredits && allOtherCreditsTooltipComponent}
                      </div>
                      {a.isComplete ? (
                        completeIconText
                      ) : (
                        <Text className="text-1 w-fit flex-1 whitespace-nowrap text-right text-gray-dark">
                          {getCreditsText(a.amount || 0, hasSubRequirements)}
                        </Text>
                      )}
                    </div>
                    {hasSubRequirements &&
                      !a.isAllOtherCredits &&
                      iterateSubRequirements(a.subRequirements!)}
                  </div>
                }
              </div>
            );
          })}
          <div ref={setContentContainer} />
        </div>
        {!!jurisdictionMessage && (
          <RichText
            className="text-1 mt-4 w-full text-left text-gray-dark"
            content={jurisdictionMessage}
          />
        )}
      </div>
    )
  );
};
export default Requirements;
